import { observer } from 'mobx-react-lite';
import { useStore } from '../../../../hooks/useStore';
import { Form, Formik, useFormikContext } from 'formik';
import CommonButton from '../../../../components/common-components/button/CommonButton';
import { FormikTextField } from '../../../../components/form-components/common-components/text-field/FormikTextField';
import { FormikFileUpload } from '../../../../components/form-components/common-components/file-upload/FormikFileUpload';
import Popup from '../../../../components/common-components/pop-up/Popup';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import styles from './NewExpensePopup.module.css';
import { Coins } from 'lucide-react';

type ExpenseFormValues = {
  amount: string;
  expenseDate: string;
  description: string;
  files: File[];
};

const PopupFooter = ({ onClose }: { onClose: () => void }) => {
  const { isSubmitting, submitForm } = useFormikContext<ExpenseFormValues>();

  return (
    <>
      <CommonButton type="cancel" onClick={onClose} disabled={isSubmitting}>
        İptal
      </CommonButton>
      <CommonButton type="save" onClick={submitForm} disabled={isSubmitting}>
        {isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
      </CommonButton>
    </>
  );
};

const validationSchema = Yup.object({
  amount: Yup.number()
    .required('Miktar zorunludur')
    .positive("Miktar 0'dan büyük olmalıdır")
    .min(0.01, 'Miktar en az 0.01 olmalıdır'),
  expenseDate: Yup.date().required('Tarih zorunludur'),
  description: Yup.string()
    .required('Açıklama zorunludur')
    .min(3, 'Açıklama en az 3 karakter olmalıdır'),
  files: Yup.array().nullable(),
});

const initialValues = {
  amount: '',
  expenseDate: new Date().toISOString().split('T')[0],
  description: '',
  files: [] as File[],
};

const NewExpensePopup = () => {
  const { popupStore, expenseStore, authStore } = useStore();

  const handleSubmit = async (values: typeof initialValues) => {
    try {
      const formData = new FormData();
      formData.append('amount', values.amount.toString());
      formData.append('expenseDate', new Date(values.expenseDate).toISOString());
      formData.append('description', values.description);

      if (values.files && values.files.length > 0) {
        values.files.forEach(file => {
          formData.append('files', file);
        });
      }

      const response = await expenseStore.createExpense(formData);

      if (!response.error) {
        popupStore.setIsNewExpenseOpen(false);
      }
    } catch (error) {
      console.error('Error creating expense:', error);
      toast.error('Harcama kaydı oluşturulurken bir hata oluştu');
    }
  };

  const handleClose = () => {
    popupStore.setIsNewExpenseOpen(false);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {() => (
        <Popup
          isOpen={popupStore.isNewExpenseOpen}
          onClose={handleClose}
          title="Yeni Harcama Kaydı"
          footer={() => <PopupFooter onClose={handleClose} />}
        >
          <div className={styles.container}>
            <Form className={styles.form}>
              <div className={styles.inputContainer}>
                <FormikTextField
                  name="amount"
                  label="Miktar (₺)"
                  type="number"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  startAdornment={<Coins size={18} className={styles.currencyIcon} />}
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField name="expenseDate" label="Harcama Tarihi" type="date" />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField
                  name="description"
                  label="Açıklama"
                  placeholder="Harcama açıklamasını giriniz..."
                  type="text"
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikFileUpload
                  name="files"
                  label="Dosyalar (İsteğe Bağlı)"
                  placeholder="Dosya seçin veya buraya sürükleyin"
                  accept=".pdf,.png,.jpeg,.jpg"
                  multiple={true}
                  maxSize={2} // 2MB
                  maxFiles={3} // Maximum 3 files
                  allowedTypes={['pdf', 'png', 'jpeg', 'jpg']}
                />
              </div>
            </Form>
          </div>
        </Popup>
      )}
    </Formik>
  );
};

export default observer(NewExpensePopup);
